// ThreeDGraph.jsx
// npm i three three-forcegraph 3d-force-graph
//
// What this version does (matches your requirements):
// 1) Uses node.data.id as the canonical identity and label anchor (label text = id by default).
// 2) Keeps KMeans nodes arranged on a big ring (like Cytoscape circle) so groups are separated.
// 3) For each KMeans node, places its connected child nodes on a *local sphere shell* around that KMeans,
//    using a Fibonacci sphere distribution to avoid overlap.
// 4) Pins those positions every tick so the force simulation can’t “tear” the grouping apart.
// 5) Preserves your edges (children should be connected to their KMeans if edges exist in `elements`).
//
// Usage:
// <ThreeDGraph
//   elements={elements}
//   kmeansRingRadius={1400}        // distance between KMeans hubs (group separation)
//   childSphereRadius={420}        // radius of child sphere around each kmeans hub
//   labelScale={0.12}
// />

import React, { useEffect, useMemo, useRef } from "react";
import ForceGraph3D from "3d-force-graph";
import * as THREE from "three";

/** --- helpers -------------------------------------------------------------- **/

function isNodeEle(ele) {
  return !!ele?.data?.id && !ele?.data?.source && !ele?.data?.target;
}

function isEdgeEle(ele) {
  return !!ele?.data?.id && !!ele?.data?.source && !!ele?.data?.target;
}

function safeStr(v) {
  return v === null || v === undefined ? "" : String(v);
}

function parseNumber(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function hexFromHslString(hsl) {
  try {
    const m = safeStr(hsl).match(
      /hsl\(\s*([0-9.]+)\s*,\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*\)/i
    );
    if (!m) return null;
    const h = parseFloat(m[1]);
    const s = parseFloat(m[2]) / 100;
    const l = parseFloat(m[3]) / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m0 = l - c / 2;

    let r = 0, g = 0, b = 0;
    if (h < 60)      { r = c; g = x; b = 0; }
    else if (h < 120){ r = x; g = c; b = 0; }
    else if (h < 180){ r = 0; g = c; b = x; }
    else if (h < 240){ r = 0; g = x; b = c; }
    else if (h < 300){ r = x; g = 0; b = c; }
    else             { r = c; g = 0; b = x; }

    const R = Math.round((r + m0) * 255);
    const G = Math.round((g + m0) * 255);
    const B = Math.round((b + m0) * 255);

    const hex = (R << 16) | (G << 8) | B;
    return `#${hex.toString(16).padStart(6, "0")}`;
  } catch {
    return null;
  }
}

function normalizeColor(c) {
  const s = safeStr(c).trim();
  if (!s) return "#999999";
  if (s.startsWith("#")) return s;
  if (s.startsWith("hsl(")) return hexFromHslString(s) || "#999999";
  return s;
}

function makeTextSprite(text, {
  fontFace = "Arial",
  fontSize = 56,
  padding = 14,
  textColor = "#ffffff",
  outlineColor = "rgba(0,0,0,0.65)",
  outlineWidth = 6,
  scale = 0.12
} = {}) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  ctx.font = `${fontSize}px ${fontFace}`;
  const metrics = ctx.measureText(text);
  const textWidth = Math.ceil(metrics.width);

  const w = textWidth + padding * 2;
  const h = fontSize + padding * 2;

  canvas.width = w;
  canvas.height = h;

  ctx.font = `${fontSize}px ${fontFace}`;
  ctx.textBaseline = "top";

  ctx.lineWidth = outlineWidth;
  ctx.strokeStyle = outlineColor;
  ctx.strokeText(text, padding, padding);

  ctx.fillStyle = textColor;
  ctx.fillText(text, padding, padding);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false
  });

  const sprite = new THREE.Sprite(material);
  sprite.scale.set(w * scale, h * scale, 1);

  return sprite;
}

// Uniform-ish points on sphere surface
function fibonacciSpherePoints(N, radius) {
  const pts = [];
  if (N <= 0) return pts;

  const phi = Math.PI * (3 - Math.sqrt(5)); // golden angle
  for (let i = 0; i < N; i++) {
    const y = 1 - (i / Math.max(1, N - 1)) * 2; // 1..-1
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = phi * i;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r;
    pts.push({ x: x * radius, y: y * radius, z: z * radius });
  }
  return pts;
}

/**
 * Core: Build nodes/links and compute:
 * - fixed position for each KMeans node on a big ring
 * - fixed position for each node that is connected to a given kmeans hub via a "kmeans" edge
 *   (child nodes arranged in a sphere around that hub)
 */
function cytoscapeTo3DGraph(elements, {
  nodeSizeDefault = 6,
  labelMode = "id",
  centerType = "kmeans",
  kmeansSphereRadius = 900,
  studentOuterSphereRadius = 260,
  studentSpherePad = 28,

  // NEW: which connections are visible as links
  visibleConnections = ["kmeans", "similarity"],

  // NEW: render controls for similarity links
  similarityOpacity = 0.22,
  similarityWidth = 0.6,
  // similarity width mapping
  similarityKey = "similarityVal", // or "weight"
  similarityMin = 0.0,
  similarityMax = 1.0,
  similarityWidthMin = 0.25,
  similarityWidthMax = 10.0,
  similarityGamma = 1.6            // >1 emphasizes high scores
} = {}) {
  const nodesRaw = (elements || []).filter(isNodeEle);
  const edgesRaw = (elements || []).filter(isEdgeEle);

  const nodeById = new Map();
  nodesRaw.forEach((ele) => {
    const d = ele.data || {};
    const id = safeStr(d.id);
    if (!id) return;

    const w = parseNumber(d.width, 40);
    const h = parseNumber(d.height, 40);
    const radius = Math.max(2, Math.min(28, Math.max(w, h) / 20 || nodeSizeDefault));

    nodeById.set(id, {
      id,
      label: safeStr(d.label || id),
      labelText: labelMode === "label" ? safeStr(d.label || id) : id,
      type: safeStr(d.type || ""),
      color: normalizeColor(d.color),
      radius,
      hubPos: null,
      childPos: null,
      hubId: null,
      x: undefined, y: undefined, z: undefined,
      vx: 0, vy: 0, vz: 0
    });
  });

  // IMPORTANT:
  // - kEdges drive layout membership (kmeans-only)
  // - visEdges are rendered (kmeans + similarity)
  const visSet = new Set(visibleConnections);

  const visEdges = edgesRaw
    .filter((ele) => visSet.has(safeStr(ele?.data?.connection)))
    .filter((ele) => nodeById.has(safeStr(ele.data.source)) && nodeById.has(safeStr(ele.data.target)));

  const kEdges = visEdges.filter((ele) => safeStr(ele?.data?.connection) === "kmeans");

  const clamp01 = (x) => Math.max(0, Math.min(1, x));
  const lerp = (a, b, t) => a + (b - a) * t;

  const links = visEdges.map((ele) => {
    const d = ele.data || {};
    const conn = safeStr(d.connection);

    const link = {
      id: safeStr(d.id),
      source: safeStr(d.source),
      target: safeStr(d.target),
      connection: conn,
      color: normalizeColor(d.color),
      width: parseNumber(d.width, 1),
      opacity: undefined
    };

    if (conn === "similarity") {
      const raw = parseNumber(d[similarityKey], NaN);

      // normalize raw score to 0..1 based on similarityMin/Max
      const t = Number.isFinite(raw)
        ? clamp01((raw - similarityMin) / Math.max(1e-9, (similarityMax - similarityMin)))
        : 0;

      // gamma curve so high scores pop more
      const tg = Math.pow(t, similarityGamma);

      link.width = lerp(similarityWidthMin, similarityWidthMax, tg);

      // optional: fade weak similarities
      link.opacity = lerp(0.08, 0.55, tg);

      link.similarity = raw;
      link.isSimilarity = true;
    }

    return link;
  });

  // Membership: hub -> children ONLY from kEdges where one endpoint is kmeans
  const childrenByHub = new Map();
  for (const ele of kEdges) {
    const d = ele.data || {};
    const sId = safeStr(d.source);
    const tId = safeStr(d.target);

    const s = nodeById.get(sId);
    const t = nodeById.get(tId);

    const sIsHub = s?.type === centerType;
    const tIsHub = t?.type === centerType;

    if (sIsHub && !tIsHub) {
      t.hubId = sId;
      if (!childrenByHub.has(sId)) childrenByHub.set(sId, []);
      childrenByHub.get(sId).push(tId);
    } else if (tIsHub && !sIsHub) {
      s.hubId = tId;
      if (!childrenByHub.has(tId)) childrenByHub.set(tId, []);
      childrenByHub.get(tId).push(sId);
    }
  }

  // Place hubs on sphere shell
  const hubs = Array.from(nodeById.values()).filter(n => n.type === centerType);
  hubs.sort((a, b) => safeStr(a.label).localeCompare(safeStr(b.label), undefined, { numeric: true, sensitivity: "base" }));

  const hubPts = fibonacciSpherePoints(hubs.length, kmeansSphereRadius);
  hubs.forEach((hub, i) => {
    const p = hubPts[i] || { x: 0, y: 0, z: 0 };
    hub.hubPos = p;
    hub.x = p.x; hub.y = p.y; hub.z = p.z;
  });

  // Place students on larger sphere around their hub
  for (const hub of hubs) {
    const childIds = childrenByHub.get(hub.id) || [];
    if (!childIds.length) continue;

    const n = childIds.length;

    let avgDia = 0;
    for (const cid of childIds) {
      const cn = nodeById.get(cid);
      avgDia += (cn?.radius || nodeSizeDefault) * 2;
    }
    avgDia = avgDia / Math.max(1, n);

    const crowdFactor = Math.max(1, Math.sqrt(n / 40));
    const R = Math.max(studentOuterSphereRadius, (avgDia + studentSpherePad) * crowdFactor * 6);

    const pts = fibonacciSpherePoints(n, R);
    const ordered = childIds.slice().sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );

    ordered.forEach((cid, idx) => {
      const child = nodeById.get(cid);
      if (!child) return;
      const p = pts[idx] || { x: 0, y: 0, z: 0 };

      child.childPos = { x: hub.hubPos.x + p.x, y: hub.hubPos.y + p.y, z: hub.hubPos.z + p.z };
      child.x = child.childPos.x; child.y = child.childPos.y; child.z = child.childPos.z;
    });
  }

  // Option: keep unassigned nodes floating, or delete them if you truly only want kmeans-driven nodes.
  nodeById.forEach((n) => {
    if (n.type === centerType) return;
    if (n.childPos) return;
    if (n.x === undefined) n.x = (Math.random() - 0.5) * 200;
    if (n.y === undefined) n.y = (Math.random() - 0.5) * 200;
    if (n.z === undefined) n.z = (Math.random() - 0.5) * 200;
  });

  return { nodes: Array.from(nodeById.values()), links };
}



/** --- component ------------------------------------------------------------ **/

export default function ThreeDGraph({
  elements,
  nodeSizeDefault = 6,
  backgroundColor = "#000000",
  labelMode = "id",           // id is your requirement
  labelScale = 0.12,

  // separation controls
  kmeansRingRadius = 1400,
  kmeansRingPlane = "xz",     // "xz" puts ring around camera nicely
  childSphereRadius = 420,
  childSpherePad = 24,

  onNodeClick,
  onNodeRightClick
}) {
  const containerRef = useRef(null);
  const fgRef = useRef(null);

  const graphData = useMemo(() => {
    return cytoscapeTo3DGraph(elements || [], {
        nodeSizeDefault,
        labelMode: "id",
        centerType: "kmeans",
        kmeansSphereRadius: 900,
        studentOuterSphereRadius: 260,
        studentSpherePad: 28,

        // NEW: render similarity edges too
         visibleConnections: ["kmeans", "similarity"],

        similarityKey: "similarityVal",
        similarityMin: 0,
        similarityMax: 1,
        similarityWidthMin: 0.25,
        similarityWidthMax: 6.0,
        similarityGamma: 1.8
    });
    }, [elements, nodeSizeDefault]);

  useEffect(() => {
    if (!containerRef.current) return;

    if (!fgRef.current) {
      const fg = ForceGraph3D()(containerRef.current);
      fgRef.current = fg;

      fg.backgroundColor(backgroundColor);
      fg.showNavInfo(false);

      // Lighting
      fg.scene().add(new THREE.AmbientLight(0xffffff, 0.85));
      const dir = new THREE.DirectionalLight(0xffffff, 0.75);
      dir.position.set(1, 1, 1);
      fg.scene().add(dir);

      // Links
      fg.linkColor((l) => l.color || "#888888");
      fg.linkWidth((l) => Math.max(0.15, Math.min(8, l.width || 1)));
    fg.linkOpacity((l) => (typeof l.opacity === "number" ? l.opacity : 0.9));

    // Optional: dotted feel for similarity (particles)
    fg.linkDirectionalParticles((l) => (l.connection === "similarity" ? 2 : 0));
    fg.linkDirectionalParticleWidth((l) => (l.connection === "similarity" ? 1.1 : 0));
    fg.linkDirectionalParticleSpeed((l) => (l.connection === "similarity" ? 0.006 : 0));


      // Tooltip
      fg.nodeLabel((n) => safeStr(n.id));

      // Node object: sphere + always-visible text sprite (id)
      fg.nodeThreeObject((node) => {
        const group = new THREE.Group();

        const geometry = new THREE.SphereGeometry(node.radius || nodeSizeDefault, 16, 16);
        const material = new THREE.MeshStandardMaterial({
          color: node.color || "#999999",
          roughness: 0.55,
          metalness: 0.1
        });

        const sphere = new THREE.Mesh(geometry, material);
        sphere.userData = { nodeId: node.id };
        group.add(sphere);

        const text = makeTextSprite(safeStr(node.id), { scale: labelScale });
        text.position.set(0, (node.radius || nodeSizeDefault) * 1.35, 0);
        group.add(text);

        group.userData = { nodeId: node.id };
        return group;
      });

      // Interaction
      fg.onNodeClick((node) => {
        if (typeof onNodeClick === "function") onNodeClick(node);

        const distance = 550;
        const distRatio = 1 + distance / Math.max(1, Math.hypot(node.x || 0, node.y || 0, node.z || 0));
        fg.cameraPosition(
          { x: (node.x || 0) * distRatio, y: (node.y || 0) * distRatio, z: (node.z || 0) * distRatio },
          node,
          700
        );
      });

      fg.onNodeRightClick((node) => {
        if (typeof onNodeRightClick === "function") onNodeRightClick(node);
      });
    }

    const fg = fgRef.current;
    fg.graphData(graphData);

    // IMPORTANT: Pin hub positions and child sphere positions each tick,
    // so the force engine cannot detach/overlap the groups.
    const origTick = fg.tickFrame;

    fg.tickFrame = () => {
        const data = fg.graphData();

        if (data?.nodes) {
            for (const n of data.nodes) {
            if (n.type === "kmeans" && n.hubPos) {
                n.x = n.hubPos.x;
                n.y = n.hubPos.y;
                n.z = n.hubPos.z;
                n.vx = 0; n.vy = 0; n.vz = 0;
            } else if (n.childPos) {
                n.x = n.childPos.x;
                n.y = n.childPos.y;
                n.z = n.childPos.z;
                n.vx = 0; n.vy = 0; n.vz = 0;
            }
            }
        }

        origTick.call(fg);
        };

    return () => {
      if (fgRef.current) fgRef.current.tickFrame = origTick;
    };
  }, [graphData, backgroundColor, nodeSizeDefault, labelScale, onNodeClick, onNodeRightClick]);

  // Resize
  useEffect(() => {
    if (!containerRef.current || !fgRef.current) return;

    const ro = new ResizeObserver(() => {
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      fgRef.current.width(w);
      fgRef.current.height(h);
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }} />;
}